import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreatives } from '@/hooks/useCreatives';
import { useCampaigns } from '@/hooks/useCampaigns';
import { deleteCreative } from '@/services/creatives';
import { formatRelativeDate } from '@/utils/date';
import type { Creative, Campaign } from '@/types/api';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';

type CreativeStatus = 'Ready' | 'Rendering' | 'Draft';

function inferStatus(c: Creative): CreativeStatus {
  // No render status in API — infer from thumbnail
  if (c.thumbnail_url) return 'Ready';
  return 'Draft';
}

const STATUS_CONFIG: Record<CreativeStatus, { classes: string; icon?: string }> = {
  Ready: {
    classes: 'bg-white text-tertiary border border-outline-variant/40',
  },
  Rendering: {
    classes: 'bg-primary-container text-primary border border-primary-fixed',
    icon: 'autorenew',
  },
  Draft: {
    classes: 'bg-surface-container-high text-on-surface-variant',
  },
};

function CreativeThumb({ creative }: { creative: Creative }) {
  if (creative.thumbnail_url) {
    return (
      <img
        src={creative.thumbnail_url}
        alt={creative.name}
        className="h-14 w-20 shrink-0 rounded-lg object-cover shadow-sm"
      />
    );
  }
  const status = inferStatus(creative);
  const colors: Record<CreativeStatus, string> = {
    Ready: 'from-primary-container to-surface-container-low',
    Rendering: 'from-primary-fixed to-primary-container',
    Draft: 'from-surface-container to-surface-container-high',
  };
  return (
    <div
      className={`h-14 w-20 shrink-0 rounded-lg shadow-sm bg-gradient-to-br ${colors[status]} flex items-center justify-center`}
    >
      <span className="material-symbols-outlined text-primary/30 text-2xl">image</span>
    </div>
  );
}

function groupByCampaign(
  creatives: Creative[],
  campaigns: Campaign[],
): Array<{ campaignName: string; items: Creative[] }> {
  if (creatives.length === 0) return [];

  // Build a map: campaign_id → name
  const campaignMap = new Map<number, string>(campaigns.map((c) => [c.id as number, c.name]));

  // Group creative by first assigned campaign
  const groups = new Map<string, Creative[]>();
  for (const cr of creatives) {
    const firstCampaignId = cr.assigned_campaign_ids?.[0];
    const campaignName =
      firstCampaignId != null
        ? (campaignMap.get(firstCampaignId) ?? `Campaign #${firstCampaignId}`)
        : 'Unassigned';
    const existing = groups.get(campaignName) ?? [];
    existing.push(cr);
    groups.set(campaignName, existing);
  }

  return Array.from(groups.entries()).map(([campaignName, items]) => ({
    campaignName,
    items,
  }));
}

const SORT_OPTIONS = ['Latest', 'Oldest', 'Name A–Z'];

export default function CreativesPage() {
  const [sortBy, setSortBy] = useState('Latest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const navigate = useNavigate();
  const { data: creatives, loading, error, refetch } = useCreatives();
  const { data: campaigns } = useCampaigns();

  const sorted = [...(creatives ?? [])].sort((a, b) => {
    if (sortBy === 'Latest')
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    if (sortBy === 'Oldest')
      return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
    return a.name.localeCompare(b.name);
  });

  const groups = groupByCampaign(sorted, campaigns ?? []);

  async function handleDelete(e: React.MouseEvent, id: number) {
    e.preventDefault();
    if (!confirm('Delete this creative?')) return;
    try {
      await deleteCreative(id);
      refetch();
    } catch {
      // silently refetch to get latest state
      refetch();
    }
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 flex-1 space-y-10">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-surface-container-highest">
        <h1 className="text-4xl font-headline font-black text-on-surface tracking-tight">
          Creatives
        </h1>
        <div className="flex items-center gap-3">
          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu((v) => !v)}
              className="flex items-center justify-between gap-2 rounded-xl h-10 px-4 bg-surface-container-low text-on-surface text-sm font-medium hover:bg-surface-variant transition-colors border border-surface-variant cursor-pointer"
            >
              <span>Sort by: {sortBy}</span>
              <span className="material-symbols-outlined text-[18px]">expand_more</span>
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-12 z-20 bg-surface-container-lowest rounded-xl shadow-xl border border-outline-variant/10 py-1 w-44">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => {
                      setSortBy(opt);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                      sortBy === opt
                        ? 'text-primary font-bold bg-primary-container'
                        : 'text-on-surface hover:bg-surface-container-low'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsNewCreativeOpen(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary-dim text-on-primary px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-primary/20 cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            New Creative
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm">
          {error}
        </div>
      )}

      {/* Skeletons */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-20 bg-surface-container-lowest rounded-xl border border-outline-variant/10 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Campaign groups */}
      {!loading &&
        groups.map(({ campaignName, items }) => (
          <section key={campaignName} className="space-y-4">
            <div className="flex items-center gap-3 px-2">
              <h3 className="text-lg font-headline font-bold text-on-surface tracking-tight">
                {campaignName === 'Unassigned' ? 'Unassigned' : `Campaign: ${campaignName}`}
              </h3>
              <span className="px-2.5 py-0.5 rounded-full bg-secondary-container text-on-secondary-container text-xs font-medium">
                {items.length} {items.length === 1 ? 'Item' : 'Items'}
              </span>
            </div>

            <div className="flex flex-col gap-3">
              {items.map((cr) => {
                const status = inferStatus(cr);
                const cfg = STATUS_CONFIG[status];
                return (
                  <Link
                    key={cr.id}
                    to={`/creatives/${cr.id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-surface-container-lowest ambient-shadow transition-transform hover:-translate-y-0.5 border border-surface-variant/20 group"
                  >
                    <div className="flex items-center gap-6">
                      <CreativeThumb creative={cr} />
                      <div className="flex flex-col justify-center">
                        <p className="text-on-surface text-base font-semibold leading-tight group-hover:text-primary transition-colors">
                          {cr.name}
                        </p>
                        <p className="text-on-surface-variant text-sm mt-1">
                          Last Edited: {formatRelativeDate(cr.updated_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div
                        className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${cfg.classes}`}
                      >
                        {cfg.icon && (
                          <span
                            className={`material-symbols-outlined text-[14px] ${status === 'Rendering' ? 'animate-spin' : ''}`}
                          >
                            {cfg.icon}
                          </span>
                        )}
                        {status === 'Ready' && (
                          <span className="w-1.5 h-1.5 rounded-full bg-tertiary" />
                        )}
                        {status}
                      </div>

                      <button
                        aria-label="Download"
                        disabled={status !== 'Ready'}
                        onClick={(e) => e.preventDefault()}
                        className={`flex items-center justify-center rounded-lg size-9 transition-colors ${
                          status === 'Ready'
                            ? 'bg-surface-container-high text-on-surface hover:bg-primary-container hover:text-primary cursor-pointer'
                            : 'text-on-surface-variant opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <span className="material-symbols-outlined text-[20px]">download</span>
                      </button>

                      <button
                        aria-label="Delete creative"
                        onClick={(e) => cr.id != null && handleDelete(e, cr.id)}
                        className="flex items-center justify-center rounded-lg size-9 text-on-surface-variant hover:bg-error-container hover:text-error transition-colors cursor-pointer"
                      >
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                      </button>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

      {/* Empty state */}
      {!loading && groups.length === 0 && !error && (
        <div className="py-24 text-center">
          <span className="material-symbols-outlined text-6xl text-primary/30">auto_awesome</span>
          <p className="mt-4 text-on-surface-variant font-medium">No creatives yet.</p>
          <button
            type="button"
            onClick={() => setIsNewCreativeOpen(true)}
            className="mt-4 inline-flex items-center gap-2 bg-primary text-on-primary px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:bg-primary-dim transition-all cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Start your first creative
          </button>
        </div>
      )}

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        onCreated={(created) => navigate(`/creatives/${created.id}`)}
      />
    </div>
  );
}
